import { Box, Pagination, Table, Title } from "@mantine/core";
import Decimal from "decimal.js";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useQuery } from "urql";
import Loading from "../Loading";
import { type ApiCostsDetailsQuery, type ApiCostsDetailsQueryVariables, apiCostsDetailsQuery } from "./queries";
import { type DaySel, externalFilter, formatDay, formatMonth, type MonthSel } from "./utils";

interface CostDetailsProps {
    selectedModel: string | null;
    selectedExternal: string | null;
    selectedMonth: MonthSel;
    selectedDay: DaySel | null;
}

function CostDetails({ selectedModel, selectedExternal, selectedMonth, selectedDay }: CostDetailsProps): JSX.Element {
    const [currentPage, setCurrentPage] = useState(1);

    // Reset page when filters change
    // biome-ignore lint/correctness/useExhaustiveDependencies: Need dependencies to trigger page reset when the filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedMonth, selectedDay, selectedModel, selectedExternal]);

    const y = selectedMonth.year;
    const m = selectedMonth.month;
    const d = selectedDay ? selectedDay.day : null;
    const { externalId, noExternal } = externalFilter(selectedExternal);

    const [res] = useQuery<ApiCostsDetailsQuery, ApiCostsDetailsQueryVariables>({
        query: apiCostsDetailsQuery,
        variables: {
            year: y,
            month: m,
            day: d,
            model: selectedModel || null,
            externalId,
            noExternal,
            page: currentPage,
        },
        requestPolicy: "cache-and-network",
    });

    const detailsTitle = selectedDay
        ? `Cost Details for ${formatDay(selectedDay.year, selectedDay.month, selectedDay.day)}`
        : `Cost Details for ${formatMonth(selectedMonth.year, selectedMonth.month)}`;
    const items = res.data?.apiCostsDetails?.items || [];
    const totalPages = res.data?.apiCostsDetails?.maxPage || 1;

    let main: JSX.Element | null = null;
    if (res.fetching) {
        main = <Loading />;
    } else if (res.error) {
        main = <Box>Error: {res.error.message}</Box>;
    } else {
        main = (
            <>
                {items.length > 0 && <Pagination total={totalPages} value={currentPage} onChange={setCurrentPage} />}
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Date</Table.Th>
                            <Table.Th>Model</Table.Th>
                            <Table.Th>External</Table.Th>
                            <Table.Th>Cost (USD)</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {items.length === 0 ? (
                            <Table.Tr>
                                <Table.Td colSpan={4} ta="center">
                                    No costs found for the selected filters.
                                </Table.Td>
                            </Table.Tr>
                        ) : (
                            items.map((cost) => (
                                <Table.Tr key={cost.id}>
                                    <Table.Td>{new Date(cost.created).toLocaleString()}</Table.Td>
                                    <Table.Td>{cost.model}</Table.Td>
                                    <Table.Td>{cost.external ? cost.external.name : "None"}</Table.Td>
                                    <Table.Td>${new Decimal(cost.cost).toFixed(6)}</Table.Td>
                                </Table.Tr>
                            ))
                        )}
                    </Table.Tbody>
                </Table>
                {items.length > 0 && <Pagination total={totalPages} value={currentPage} onChange={setCurrentPage} />}
            </>
        );
    }

    return (
        <>
            <Title order={4} mt="xl">
                {detailsTitle}
            </Title>
            {main}
        </>
    );
}

export default CostDetails;
